import React, { useState, useEffect, useMemo } from "react";
import { useUsers } from "@api/usersController";
import { getRoles } from "@api/rolesController";
import { Button, Input, SimpleTable } from "@components";
import {
  SectionHeader,
  SectionTitle,
  SectionTagline,
  ControlStrip,
} from "../components";
import AddTeamMember from "../modals/AddTeamMember";
import EditTeamMember from "../modals/EditTeamMember";
import { PlusLg, Search } from "react-bootstrap-icons";
import { useFuseSearch } from "@hooks";
import DeleteTeamMember from "../modals/DeleteTeamMember";
import { useUser, useMerchant } from "@auth";

const searchKeys = ["name", "email", "role", "branch"];

function Team() {
  const { user } = useUser();
  const { merchant } = useMerchant();
  const { data: rawUsers = [], refetch: refetchUsers } = useUsers();
  const users = useMemo(
    () => [...rawUsers].sort((a, b) => a.name.localeCompare(b.name)),
    [rawUsers],
  );
  const [roles, setRoles] = useState([]);
  const [addTeamMemberIsOpen, setAddTeamMemberIsOpen] = useState(false);
  const [editTeamMemberDetails, setEditTeamMemberDetails] = useState(null);
  const [deleteTeamMemberDetails, setDeleteTeamMemberDetails] = useState(null);

  const {
    query,
    setQuery,
    results: filteredUsers,
  } = useFuseSearch(users, searchKeys);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchData = () => {
    refetchUsers();
    fetchRoles();
  };

  const fetchRoles = async () => {
    const data = await getRoles();
    let roleSelectData = data.map((x) => ({
      ...x,
      value: x.id,
      display: x.name,
    }));
    roleSelectData.unshift({ value: "", display: "Please select" });
    setRoles(roleSelectData);
  };

  const tableConfig = () => ({
    rowActions: [
      {
        label: "Edit",
        onClick: (x) => setEditTeamMemberDetails(x),
      },
      {
        label: "Delete",
        onClick: (x) => setDeleteTeamMemberDetails(x),
      },
    ],
    columns: [
      { title: "Name", width: "144px", render: (x) => x.name },
      { title: "Email", width: "255px", render: (x) => x.email },
      {
        title: "Role",
        alignment: "left",
        width: "133px",
        render: (x) => x.role,
      },
      {
        title: "Branch",
        alignment: "left",
        width: "133px",
        render: (x) => x.branch,
      },
    ],
  });

  const onModalClose = () => {
    setAddTeamMemberIsOpen(false);
    setEditTeamMemberDetails(null);
    setDeleteTeamMemberDetails(null);
  };

  return (
    <div className="flex flex-col items-center py-4 px-5 border-b border-zinc-200 overflow-hidden grow">
      <div className="py-8 w-full max-w-4xl grow overflow-hidden flex flex-col">
        <SectionHeader>
          <SectionTitle>Team</SectionTitle>
          <SectionTagline>
            Manage workspace members, set access levels, and invite new team
            members
          </SectionTagline>
          {user.memberships.length > 1 && (
            <SectionTagline>
              These changes apply to your account with <b>{merchant.name}</b>
            </SectionTagline>
          )}
        </SectionHeader>
        <ControlStrip>
          <div className="grow max-w-72">
            <Input
              placeholder="Search by team member"
              prefix={<Search />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button onClick={() => setAddTeamMemberIsOpen(true)} color="primary">
            <PlusLg className="mr-2" />
            Add member
          </Button>
        </ControlStrip>
        <SimpleTable config={tableConfig()} data={filteredUsers} extraCol />
        <AddTeamMember
          open={addTeamMemberIsOpen}
          onClose={onModalClose}
          onComplete={fetchData}
          roles={roles}
        />
        {!!editTeamMemberDetails && (
          <EditTeamMember
            editTeamMemberDetails={editTeamMemberDetails}
            onClose={onModalClose}
            onComplete={fetchData}
            roles={roles}
          />
        )}
        {!!deleteTeamMemberDetails && (
          <DeleteTeamMember
            user={deleteTeamMemberDetails}
            open={!!deleteTeamMemberDetails}
            onClose={onModalClose}
            onComplete={fetchData}
          />
        )}
      </div>
    </div>
  );
}

export default Team;
